import { Mesh, Plane, Quaternion, Vector3, type Material } from 'three';
import type { FrameObject } from './FrameObject';
import { EAR_Z_FRONT } from './HeadOccluder';

/**
 * Temples end at the ear.
 *
 * A real temple runs over the ear root and continues behind the pinna, where
 * nobody sees it. The head occluder models the ear as a block, but the tracked
 * head and the rendered one never match exactly: as soon as the head turns, the
 * end of the temple (the bend and the tip) shows beside or behind the real ear,
 * floating in the air. So the temples are simply not drawn beyond the ear: one
 * clipping plane — the coronal plane of the head through the front of the
 * occluder's ear block, the ear root — discards every fragment of the temple
 * subtrees behind it, in every pass (lit draw, acetate depth prepass and
 * transmission, shadow map). The cut is the front face of the ear block, so
 * the temple visibly ends where it enters the ear, with or without the
 * occluder: no tip, no bend, no gap. The camera is always in front of that
 * plane, so the open end of a clipped temple is never seen from inside.
 *
 * One plane, on every material of the frame: only the temples ever reach
 * behind the ear (the front and the end pieces sit ~90 mm in front of it), and
 * the materials are shared — between the two temples, between temples and end
 * pieces, and the library-acetate depth prepass with the front — so a plane per
 * temple would cut the other one. Clipping the whole frame alike also keeps a
 * single shader program per material kind (a clipped temple next to an
 * unclipped front would compile every program twice).
 *
 * The plane is placed in head axes (frame origin, pantoscopic tilt undone),
 * exactly like the occluder's ear blocks, from the pose the frame and the
 * occluder share: resizing the frame (root scale) or opening the temples never
 * moves it, as neither moves the wearer's ears.
 */

/**
 * Depth of the cut behind the front of the occluder's ear block (mm). Flush
 * with it, at the ear root: where the temple runs inside the block, the block
 * front and the cut coincide, and many temples are taller than the modelled
 * helix (their top edge rises up to ~6 mm above the block), so a deeper cut
 * would leave a stub of temple sticking out over the ear.
 */
export const TEMPLE_CUT_BEHIND_EAR_MM = 0;

/** Head-axes z of the cut (mm from the frame origin, negative = behind the front). */
export function templeCutZ(): number {
  return EAR_Z_FRONT - TEMPLE_CUT_BEHIND_EAR_MM;
}

const X_AXIS = new Vector3(1, 0, 0);
const tmpTilt = new Quaternion();
const tmpHead = new Quaternion();
const tmpNormal = new Vector3();
const tmpPoint = new Vector3();

/**
 * World-space cut plane for one frame pose. Its positive side (kept by three's
 * clipping) is the front of the head: points whose head-axes z is ≥ the cut.
 * @param position frame origin (world / camera space, mm) — the occluder's position
 * @param quaternion frame orientation, including the pantoscopic tilt — the occluder's orientation
 * @param pantoscopicTiltDeg tilt of the front, undone for the head axes (HeadOccluder.pose)
 */
export function templeCutPlane(position: Vector3, quaternion: Quaternion, pantoscopicTiltDeg: number, out: Plane): Plane {
  tmpTilt.setFromAxisAngle(X_AXIS, (-pantoscopicTiltDeg * Math.PI) / 180);
  tmpHead.copy(quaternion).multiply(tmpTilt);
  tmpNormal.set(0, 0, 1).applyQuaternion(tmpHead);
  tmpPoint.set(0, 0, templeCutZ()).applyQuaternion(tmpHead).add(position);
  return out.setFromNormalAndCoplanarPoint(tmpNormal, tmpPoint);
}

/** Calls `visit` once for every material drawn by `frame` (dressing children included). */
export function forEachFrameMaterial(frame: FrameObject, visit: (m: Material) => void): void {
  const seen = new Set<Material>();
  frame.root.traverse(o => {
    if (!(o instanceof Mesh)) return;
    for (const m of (Array.isArray(o.material) ? o.material : [o.material]) as Material[]) {
      if (seen.has(m)) continue;
      seen.add(m);
      visit(m);
    }
  });
}

/**
 * Keeps the cut plane on the frame, render after render. The same plane array
 * is assigned to every material on each update, so materials created later
 * (library acetate change, frame swap) pick it up on their first frame; three
 * recompiles a material once when its number of planes changes. Requires
 * `renderer.localClippingEnabled`.
 */
export class TempleClip {
  /** A/B and debug switch: false draws the temples whole (previous behaviour). */
  enabled = true;
  /** Until the first pose: far behind everything, clips nothing. */
  readonly plane = new Plane(new Vector3(0, 0, 1), 1e6);
  private readonly planes: Plane[] = [this.plane];
  private readonly none: Plane[] = [];

  /** A110Y: what the last attach() covered — the traversal is repeated only when the materials changed. */
  private attached: { frame: FrameObject; version: number; enabled: boolean } | null = null;

  /** Gives every material of `frame` the cut (or none when disabled), e.g. on a frame swap before its first render. */
  attach(frame: FrameObject): void {
    const planes = this.enabled ? this.planes : this.none;
    forEachFrameMaterial(frame, m => {
      if (m.clippingPlanes === planes) return;
      m.clippingPlanes = planes;
      // The cut-off end must not cast its shadow onto the face either.
      m.clipShadows = true;
    });
    this.attached = { frame, version: frame.materialsVersion, enabled: this.enabled };
  }

  /** Places the cut for the pose of this render and keeps it on the frame's materials. */
  update(frame: FrameObject, position: Vector3, quaternion: Quaternion, pantoscopicTiltDeg: number): void {
    templeCutPlane(position, quaternion, pantoscopicTiltDeg, this.plane);
    // The plane object is shared by reference: only new materials need the traversal.
    const a = this.attached;
    if (!a || a.frame !== frame || a.version !== frame.materialsVersion || a.enabled !== this.enabled) this.attach(frame);
  }
}
